import { GUI } from "./GUI";
import { BackgroundManager } from "./BackgroundManager";
import { HUD } from "./HUD";
import { Board } from "./cards/Board";
import { GameModel } from "../GameModel";
import { DevelopmentLayer } from "./DevelopmentLayer";
import { HelpLayer } from "./HelpLayer";
import { ResignLayer } from "./ResignLayer";
import { StartLayer } from "./StartLayer";
import { GameOverLayer } from "./GameOverLayer";
import { AudioManager } from "../AudioManager";
import { GameConstants } from "../GameConstants";
import { GameVars } from "../GameVars";
import { GameWonLayer } from "./GameWonLayer";

export class GameState extends Phaser.State {

    public static currentInstance: GameState;    
    public static startLayerShown: boolean = false;
    
    public gui: GUI;
        
    private backgroundManager: BackgroundManager;
    private hud: HUD;
    private board: Board;
    private gameModel: GameModel;
    private developmentLayer: DevelopmentLayer;
    private helpLayer: HelpLayer;
    private resignLayer: ResignLayer;
    private startLayer: StartLayer;
    private gameOverLayer: GameOverLayer;
    
    public init(): void {
        
        GameState.currentInstance = this;
        
        this.developmentLayer = null;
    }
    
    public create(): void {
        
        this.gameModel = new GameModel(this.game);

        if (GameConstants.SPONSOR === GameConstants.GAME_DISTRIBUTION) {
            GameModel.currentInstance.initGameDistributionSDK();
        }
        
        this.gameOverLayer = null;
        
        this.backgroundManager = new BackgroundManager(this.game);
        this.add.existing(this.backgroundManager);

        this.gui = new GUI(this.game);
        this.add.existing(this.gui);
        
        this.hud = new HUD(this.game);
        this.add.existing(this.hud);
        
        this.board = new Board(this.game);
        this.add.existing(this.board);   
        
        if (!GameState.startLayerShown) {
            
            this.pause();
            
            GameState.startLayerShown = true;
            
            this.startLayer = new StartLayer(this.game);
            this.add.existing(this.startLayer);
            
            this.game.time.events.add(250, function(): void {
                this.startLayer.animate();
            }, this);
            
            // audio
            AudioManager.getInstance().playSound("soundtrack");

        } else {
            
            this.pause();
            
            this.startLayer = null;
            this.startGame();
        }

        if (!this.game.device.desktop && GameConstants.SPONSOR !== GameConstants.YANDEX) {
            this.game.input.onDown.add(this.goLarge, this);
        }

        if (GameConstants.SPONSOR === GameConstants.FUNO) {
            this.game.sound.mute = true;
            this.game.input.onDown.addOnce(function(): void {
                this.game.sound.mute = false;
            }, this);
        }
    }

    public shutdown(): void {
        
        GameState.currentInstance = null;
        
        this.gameModel.destroy();
        
        super.shutdown(this.game);
    }

    public goLarge(): void {

        this.game.scale.startFullScreen();
    }
    
    public startGame(): void {
        
        if (this.startLayer) { 
            this.startLayer.destroy();
        }
        
        if (GameVars.getInstance().simulatesStartedGame || GameVars.getInstance().retrieveSavedGame) {
            this.onCardsDealt();
            
            if (GameVars.getInstance().retrieveSavedGame) {
                GameModel.currentInstance.onGameRetrieved();
            }
            
        } else {
            this.board.dealCards();
        }  
    }
    
    public onCardsDealt(): void {

        if (GameConstants.SPONSOR === GameConstants.GAME_DISTRIBUTION) {
            GameModel.currentInstance.loadRewardedAd();
        }
        
        this.resume();
        this.hud.startTimer();
    }
    
    public onClickResign(): void {
        
        this.board.visible = false;
        
        this.hud.pause();
        this.gui.pause();
        
        this.resignLayer = new ResignLayer(this.game);
        this.add.existing(this.resignLayer);
    }
    
    public resign(): void {
        
        this.game.state.restart(true, false);
    }
    
    public retrieveGameState(): void {
        this.game.state.restart(true, false);
    }
    
    public replay(): void {
        
        this.game.state.restart(true, false);
    }
    
    public noResign(): void {
        
        this.board.visible = true;
        
        this.hud.resume();
        this.gui.resume();
        
        this.resignLayer.destroy();
    }
    
    public help(): void {
        
        this.board.visible = false;
        
        this.hud.pause();
        this.gui.pause();
        
        this.helpLayer = new HelpLayer(this.game);
        this.add.existing(this.helpLayer);
    }
    
    public closeHelpMenu(): void {
        
        this.board.visible = true;
        
        this.hud.resume();
        this.gui.resume();
        
        this.helpLayer.destroy();
    }
    
    public pause(): void {
        
        this.gui.pause();
        this.hud.pause();
        this.board.pause();
    }
    
    public resume(): void {
        
        this.gui.resume();
        this.hud.resume();
        this.board.resume();
    }
    
    public showDevelopmentLayer(): void {
        
        this.pause();

        this.developmentLayer = new DevelopmentLayer(this.game);
        this.add.existing(this.developmentLayer);
    }
    
    public closeDevelopmentLayer(): void {
        
        this.resume();
                    
        this.gui.developmentMenuButton.visible = true;
        this.developmentLayer.destroy();
    }
    
    public reshuffle(): void {
        
        this.board.reshuffle();
    }
    
    public undo(): void {
        
        this.board.undo();
        this.gui.undo();
    }
    
    public gameWon(): void {
        
        this.pause();
        
        if (this.developmentLayer) {
            this.developmentLayer.destroy();
        }

        this.game.time.events.add(1000, function(): void{
            this.gameWonLayer = new GameWonLayer(this.game);
            this.add.existing(this.gameWonLayer);
        }, this); 
        
        // audio
        AudioManager.getInstance().playSound("congratulations");  
    }
    
    public gameLost(): void {
        
        this.pause();
        
        if (this.developmentLayer) {
            this.developmentLayer.destroy();
        }
        
        this.gameOverLayer = new GameOverLayer(this.game);
        this.add.existing(this.gameOverLayer); 
        
        // audio
        AudioManager.getInstance().playSound("gameover");    
    }
    
    public onHint(outerPileIndex: number): void {
        
        if (outerPileIndex > -1 ) {
            this.board.showHint(outerPileIndex);
        }
    }
    
    public resizeFullScreen(): void {
        
        this.board.scale.x = GameVars.scaleX;
        
        this.gui.resizeFullScreen();
        this.hud.resizeFullScreen();
    }
}
